import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    generatePath,
    matchPath,
    Navigate,
    Outlet,
    Route,
    Routes,
    useLocation,
    useNavigate,
    useSearchParams
} from "react-router-dom";
import Form from "../../components/form/Form";
import {useProjectForm} from "../../providers/ProjectFormProvider";
import PositionFormProvider, {usePositionForm} from "../../providers/PositionFormProvider";
import LoadingBanner from "../../components/LoadingBanner";
import {useMount} from "react-use";
import PositionFullForm from "./position-form/PositionFullForm";
import PositionTitle from "./position-form/PositionTitle";
import PositionTiming from "./position-form/PositionTiming";
import PositionDisciplines from "./position-form/PositionDisciplines";
import PositionSkills from "./position-form/PositionSkills";
import PositionIndustries from "./position-form/PositionIndustries";
import PositionJobSpecification from "./position-form/PositionJobSpecification";
import ReactDOM from "react-dom";
import {Divider} from "antd";
import UserDiscipline from "../../models/UserDiscipline";
import PositionBrands from "./position-form/PositionBrands";

const PositionWizard = React.forwardRef((props, formRef) => {

    const {
        project,
        onFormReset: onProjectFormReset
    } = useProjectForm();
    const {
        position,
        formModel,
        isLoading,
        isDisabled,
        onFormChange,
        isProjectUpdating,
        onFormReset,
        onFormSubmit,
        canCopyPrevious,
        copyPreviousPosition
    } = usePositionForm();


    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();


    const hasRedirect = searchParams.has('fromReview') || searchParams.has('fromBrief');
    const isSetup = useMemo(() => matchPath({path: 'client/project/setup/:projectId/role/:roleId/*'},location.pathname), [location?.pathname]);
    const isBidding = useMemo(() => matchPath({path: 'client/project/bidding/:projectId/brief/role-requirements/role/:roleId/*'},location.pathname), [location?.pathname]);

    const steps = formModel?.stepRoutes() || [];
    const titleRef = useRef(null);

    const isNew = useMemo(() => {
        return !position?.id || position?.id === 'new' || TB.isNaN(parseInt(position?.id))
    }, [position?.id])
    const currentStep = useMemo(() => {

        let match = isSetup || isBidding;
        if (isSetup) {
            match = matchPath({path: 'client/project/setup/:projectId/role/:roleId/:step'},location.pathname);
        }else if (isBidding) {
            match = matchPath({path: 'client/project/bidding/:projectId/brief/role-requirements/role/:roleId/:step'},location.pathname);
        }
        return match?.params?.step
    }, [isSetup, isBidding]);

    const currentIndex = useMemo(() => {
        return steps.indexOf(currentStep)
    }, [steps, currentStep]);

    const [isManaging, setIsManaging] = useState(() => (!currentStep || (formModel?.id && !formModel?.needsSetup())));



    const isReadOnly = useMemo(() => {
        return position?.hasBeenFilled()
    }, [position]);


    useEffect(() => {
        if (formRef?.current?.isFormSubmitted?.()) {
            formRef?.current?.forceReset()
        }
    }, [currentStep]);


    useMount(() => {
        let pathname = false;

        if (formModel && !formModel.currentRequiredStep() && currentStep) {
            pathname = './';
            setIsManaging(true)
        }
        if(formModel && formModel.currentRequiredStep() && formModel.currentRequiredStep() !== currentStep) {
            pathname = formModel.currentRequiredStep();
            setIsManaging(false)
        }
        if (pathname) {
            console.log('PositionWizardRedirect!', pathname)
            return navigate({
                ...location,
                pathname
            }, {
                replace: true,
                state: location.state || {}
            })
        }
    })
    const onChange = (params = {}) => {

        console.log('onChange', params)
        if (isProjectUpdating || isReadOnly) return;
        let update = {...formModel, ...params}

        // Reset end date if start date changes moves ahead
        if (update.ended_at || update.started_at) {
            let updatedEndDate = TB.moment(update.ended_at);
            let currentStartDate = TB.moment(update.started_at || position?.started_at);
            if (!updatedEndDate.isValid() || TB.moment(currentStartDate).isSameOrAfter(updatedEndDate, 'day')) {
                update.ended_at = null;
            }
        }

        // format avg_hours_per_period to integer
        if (params.avg_hours_per_period && params.avg_hours_per_period !== formModel.avg_hours_per_period) {
            update.avg_hours_per_period = parseInt(params.avg_hours_per_period);
        }
        if (params.budget && params.budget !== formModel.budget) {
            let weeks = formModel.calculatedTotalWeeks();
            const platformDisciplineRate = params.target_rate || TB.max(
                TB.map(formModel.disciplines, dis => {
                        return UserDiscipline.rateForDiscipline(dis.id, parseFloat(formModel.percent_markup || 0), true)
                    }
                ))
            let avg_hours = Math.floor((params.budget / platformDisciplineRate) / weeks);
            if (avg_hours > 1) {
                update.avg_hours_per_period = Math.floor((params.budget / platformDisciplineRate) / weeks);
            }
        }

        // Reset required on site days per week if required_onsite is false otherwise format to integer
        if (formModel.required_onsite && TB.isDefined(params.required_onsite) && !params.required_onsite) {
            update.onsite_days_per_week = null;
        }else if (params.onsite_days_per_week) {
            update.onsite_days_per_week = parseInt(params.onsite_days_per_week);
        }

        // Add required skills and disciplines for each template
        if (params.templates?.length) {
            let templateSkills = position?.getRoleRequiredSkills(params.templates);
            let templateDisciplines = position?.getRoleRequiredDisciplines(params.templates)
            // only add and update new skills/disciplines from each template vs. existing position's set skills/disciplines
            update.skills = TB.unionBy(formModel.skills || [],templateSkills, 'id');
            update.disciplines = templateDisciplines;//TB.unionBy(formModel.disciplines || [], templateDisciplines, 'id');
        }

        //Remove total hours from deliverables
        if (params.deliverables?.length) {
            update.deliverables = TB.map(params.deliverables || [], deliverable => {
                return {
                    ...deliverable,
                    total_hours: deliverable.total_hours || undefined
                }
            });
        }

        // calculate end date for fixed retainer
        if (project.isFixedRetainer()) {
            update.ended_at = position.calculateFixedRetainedEstimatedEndDate(
                update.started_at,
                update.payout_frequency,
                update.actual_total_periods
            )
            if (formModel?.isFixedRetainerRateBased?.() && update?.payout_frequency === 'weekly' && update?.avg_hours_per_period > 40) {
                update.avg_hours_per_period = 40;
            }
        }

        return onFormChange(update)
    }


    const invalidFields = () => {
        if (!formRef.current) {
            return false
        }
        let invalid = formRef.current.forceValidate();
        if (currentStep && !formModel?.title?.length) {
            invalid = titleRef.current;
        }

        if (invalid) {
            const node = ReactDOM.findDOMNode(invalid);

            $('html, body').animate({scrollTop: $(node).offset().top - 100}, 1000);
        }
        return invalid;
    }

    const onSubmit = async () => {
        if (isReadOnly || isProjectUpdating) return;

        let invalidField = await invalidFields();

        console.log({invalidField})
        if (invalidField) {
            return
        }

        // if (formModel.templates?.length && position.templates.length) {
        //     let selectedTemplateIds = TB.map(formModel.templates, 'id');
        //     let removedTemplate = TB.find(position.templates, template => !selectedTemplateIds.includes(template.id));
        //
        // }
        let didUpdate = formModel.needsSetup() ? true : await onFormSubmit(formModel);

        // console.log('onSubmit', didUpdate, currentStep);

        if(!didUpdate) return;

        if (currentStep) {
            let pathname = './';

            if ((!isManaging || !formModel.id) && steps[currentIndex + 1]) {
                pathname = steps[currentIndex + 1];
            }
            // console.log('onSubmit', currentIndex, steps[currentIndex], steps[currentIndex+1])
            return navigate({
                ...location,
                pathname
            }, {
                replace: true,
                state: location.state || {}
            })
        }else {
            console.log('onFinish', didUpdate, currentStep);
            props.onFinish()
        }
    }

    const isFormComplete = useMemo(() => {
        if (!formModel || isLoading) return true;

        let validations = {
            title: formModel.hasSetTitle(),
            'wripple-roles': formModel.templates?.length > 0,
            'timing-and-budget': formModel.hasSetTerms(),
            'skills': formModel.skills?.length > 0,
            'industry-experience': true,//position.industries?.length > 0,
            'brands': true,//position.industries?.length > 0,
            'job-specification': formModel.hasSelectedExperience()
        }

        if (currentStep) {
            return validations[currentStep];
        }
        let invalid = false;

        return !invalid
    }, [currentStep,
        isLoading,
        formModel?.title,
        formModel?.budget,
        formModel?.deliverables?.length,
        formModel?.started_at,
        formModel?.ended_at,
        formModel?.actual_total_periods,
        formModel?.payout_amount,
        formModel?.payout_frequency,
        formModel?.target_rate,
        formModel?.budget,
        formModel?.avg_hours_per_period,
        formModel?.required_onsite,
        formModel?.onsite_days_per_week,
        formModel?.templates?.length,
        formModel?.skills?.length,
        formModel?.wants_intermediate_experience,
        formModel?.wants_senior_experience,
        formModel?.wants_junior_experience
    ]);

    const canCancel = useMemo(() => {

        if (isBidding && currentStep) return true;

        if(isBidding && !currentStep && !formModel?.needsSetup()) {
            return true;
        }
        if (currentStep && isManaging) return true;

        return hasRedirect && (!currentStep || !(formModel?.id && !formModel.needsSetup()));
    }, [isBidding, isSetup, currentStep]);

    const onCancel = async() => {


        let pathname;
        if ((isBidding || !formModel.needsSetup()) && currentStep) {
            pathname = './'
        }else if (isBidding && !currentStep) {
            pathname = './../'
        }
        if (searchParams.has('fromReview')) {
            pathname = generatePath('/client/project/setup/:projectId/review', {
                projectId: project.id
            })

            return navigate(pathname, {replace: true})
        }else if (searchParams.has('fromBrief')) {
            pathname = generatePath('/client/project/bidding/:projectId/brief/role-requirements', {
                projectId: project.id
            })

            return navigate(pathname, {replace: true})
        }
        await onFormReset();

        return navigate({
            ...location,
            pathname
        }, {
            replace: true,
            state: location.state || {}
        })
    }

    const goBack = async () => {
        if (canCancel) return onCancel();

        console.log('goBack', {isNew})
        // await onFormReset();

        if (isNew && currentStep && steps[currentIndex - 1]) {
            let pathname = steps[currentIndex - 1];
            return navigate({
                ...location,
                pathname
            }, {
                replace: true,
                state: location.state || {}
            })
        } else {
            return props.goBack()
        }
    }

    // console.info({isSetup, isBidding})
    if (isLoading) {
        return <LoadingBanner message="Loading" adjustOverlay/>;
    }

    if (!formModel) return null;

    if (!isDisabled && position && position?.needsSetup() && formModel && !formModel.isAppropriateStep(currentStep)) {
        return <Navigate to={{...location, pathname: formModel.currentRequiredStep()}} replace={true} state={location.state || {}}/>
    }
    if (isNew && !project.isHiringSourceWizard() && !project.isHiringSourceQuickHireOpenToTalent()) {
        return <Navigate to={project.detailLink()} replace={true} />
    }

    const nextStepTitle = formModel.steps()[currentIndex + 1]

    return (
        <React.Fragment>

            {project.positions?.length >= 1 && currentStep === 'title' && !isBidding && (
                <React.Fragment>
                    <div className="flex flex-col items-center self-center bg-white px-2 py-1">
                        <div className="items-center self-stretch justify-center flex flex-row p-3 gap-5">
                            <h5 className="mb-0 mx-2">No additional positions needed for this project?</h5>
                            <button className="btn btn-primary pl-2"
                                    type="button"
                                    disabled={props.isDisabled || isProjectUpdating}
                                    onClick={async () => {
                                        await onProjectFormReset()
                                        await props.onFinish();
                                    }}>
                                Continue to summary
                            </button>
                        </div>
                    </div>
                    <Divider className="my-4">More positions to fill? Add another position below.</Divider>
                </React.Fragment>
            )}
            {!isBidding && currentStep && currentStep !== 'title' && (
                <div className="flex flex-col items-stretch">

                    <div ref={titleRef} className="flex flex-col items-stretch bg-white px-2 py-1" >
                        {
                            TB.isDefined(props.positionIndex) && (
                                <p className="byo-position-nav-label mb-2">
                                    Position <b className="current-position">#{props.positionIndex}</b>
                                </p>
                            )
                        }
                        <div className="field position-text-field">
                            <div className="form-group transition-all ease-in flex flex-col">
                                <input type="text" className="form-control"
                                       name="title"
                                       onChange={event => onChange({title: event.target.value})}
                                       value={formModel.title || ''}
                                       placeholder="Open position name"
                                       autoComplete="off"
                                       maxLength={85}
                                       disabled={props.isDisabled}
                                />
                                <div className="invalid-feedback block transition-all ease-in"
                                     style={!formModel?.title?.length ? {opacity: 1,height: 30}:{opacity: 0,height: 0}}>
                                    The position name field if required
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="position-wizard-progress" id="position-wizard-progress">

                        {TB.map(formModel.steps(), (positionStep, stepIndex) => {
                            if (positionStep === 'Title') return null;
                            return (
                                <div key={positionStep} className={TB.mapClass("position-progress-item", {
                                    "active": currentStep === TB.slugify(positionStep),
                                    "complete": stepIndex < currentIndex
                                })}>
                                    <h5>{positionStep}</h5>
                                </div>
                            )
                        })}

                    </div>
                </div>
            )}
            <div className="position-wizard-step">

                <Form
                    ref={formRef}
                    model={formModel}
                    formProps={{
                        onKeyDown: (e) => (e.key === 'Enter' || e.keyCode === 13) && e.preventDefault()
                    }}
                    onChange={onChange}
                    mapping={mapped => {
                        return {
                            ...mapped,
                            started_at: mapped.started_at ? TB.moment(mapped.started_at).toISOString() : undefined,
                            ended_at: mapped.ended_at ? TB.moment(mapped.ended_at).toISOString() : undefined,
                        }
                    }}
                    submitButton={false}
                    cancelButton={false}
                    onSubmit={onSubmit}
                    className={TB.mapClass("position-step", {isReadOnly})}
                    fields={[]}
                    isDisabled={props.isDisabled}>
                    {isSetup && !currentStep && !isNew && (
                        <p className="font-medium text-lg mt-[-25px] underline mb-4 text-center">Review and confirm the details below to continue</p>
                    )}
                    <Outlet
                        context={{
                            isDisabled: props.isDisabled || isProjectUpdating,
                            isReadOnly,
                            onChange: onChange,
                            onSubmit: onSubmit,
                            positionIndex: props.positionIndex
                        }}
                    />

                    {!isReadOnly && (
                        <div className="flex self-stretch items-center justify-end gap-3 mt-5">
                            {canCopyPrevious && currentStep === 'title' && (
                                <button formNoValidate
                                        className="btn btn-outline-dark mr-auto"
                                        type="button"
                                        disabled={props.isDisabled || isProjectUpdating || !isFormComplete}
                                        onClick={copyPreviousPosition}>
                                    Copy from previous role
                                </button>
                            )}
                            <button formNoValidate
                                    className="btn btn-outline-dark"
                                    disabled={props.isDisabled || isProjectUpdating}
                                    onClick={goBack}
                                    type="button">
                                {canCancel ? 'Cancel' : 'Go Back'}
                            </button>

                            <button
                                formNoValidate
                                onClick={onSubmit}
                                className="btn btn-primary"
                                disabled={props.isDisabled || isProjectUpdating || !isFormComplete}
                                type="button">
                                {isSetup && (
                                    <React.Fragment>
                                        {(!currentStep || isManaging) ? 'Save & Continue' : (nextStepTitle ? `Next: Define ${nextStepTitle}` : 'Save & Review Role')}
                                    </React.Fragment>
                                )}
                                {isBidding && 'Update'}
                            </button>
                        </div>
                    )}
                </Form>
            </div>
        </React.Fragment>
    )
})


const PositionWizardLayout = ({roleId, ...props}) => {

    const {
        project,
        isLoading
    } = useProjectForm();

    const formRef = useRef(null);
    if (isLoading || !project) {
        return null;
    }

    return (
        <PositionFormProvider positionId={roleId} formRef={formRef}>
            <Routes>
                <Route path={'*'} element={<PositionWizard{...props} ref={formRef}/>}>
                    <Route index element={<PositionFullForm/>}/>
                    <Route path="title" element={<PositionTitle/>}/>
                    <Route path="wripple-roles" element={<PositionDisciplines/>}/>
                    <Route path="timing-and-budget" element={<PositionTiming/>}/>
                    <Route path="skills" element={<PositionSkills/>}/>
                    <Route path="industry-experience" element={<PositionIndustries/>}/>
                    <Route path="brands" element={<PositionBrands/>}/>
                    <Route path="job-specification" element={<PositionJobSpecification/>}/>
                </Route>
            </Routes>

        </PositionFormProvider>
    )
}

export {
    PositionWizardLayout
}
PositionWizard.propTypes = {};

export default PositionWizard;